import {
  ACCOUNT_ROLES,
  type AccountRole,
  capitalizeWords,
  isDefined,
  type ReportColumnDefinition,
  type ReportKpiType,
} from '@terros/common'
import { type ReportColumnVariantGroup } from '../column'

export const addRoleToColumnLabel = (
  column: ReportColumnDefinition,
  allColumns: Set<ReportKpiType>
): ReportColumnDefinition => {
  if (column.variant) return column
  return {
    ...column,
    label: updateColumnLabel(column, allColumns),
  }
}

export function updateColumnLabel(
  column: Pick<ReportColumnDefinition, 'value' | 'label'>,
  allColumns: Set<ReportKpiType>
): string {
  const { isRoleColumn, type } = isAccountRoleColumn(column)

  if (!isRoleColumn) return column.label

  const otherColumn = toOtherRoleColumn(column.value)

  const hasDifferentiation = allColumns.has(otherColumn)

  if (!hasDifferentiation) return column.label

  if (type === 'setter') return `${column.label} by Setters`

  if (type === 'closer') return `${column.label} by Closers`

  return column.label
}

export const isAccountRoleColumn = (
  col: Pick<ReportColumnDefinition, 'value' | 'group'>
): { isRoleColumn: boolean; type: AccountRole } => {
  const role = ACCOUNT_ROLES.find((r) => col.group?.toLowerCase() === r || col.value.includes(r))
  const type = role ?? 'setter'
  return { isRoleColumn: isDefined(role), type }
}

export const toOtherRoleColumn = (value: string): string => {
  if (value.includes('setter')) return value.replace('setter', 'closer')
  return value.replace('closer', 'setter')
}

export const toOtherRole = (role: AccountRole): AccountRole => {
  return role === 'setter' ? 'closer' : 'setter'
}

export function stripRoleFromDescription(label: string): string
export function stripRoleFromDescription(label: string | undefined): string | undefined
export function stripRoleFromDescription(label: string | undefined): string | undefined {
  return label
    ?.replaceAll('by setter', '')
    .replaceAll('by closer', '')
    .replaceAll('(S)', '')
    .replaceAll('(C)', '')
    .trim()
}

export function addVariantToColumnLabel(
  column: ReportColumnDefinition,
  selectedColumns: Set<string>,
  variantMap: Map<string, ReportColumnVariantGroup>
): ReportColumnDefinition {
  const { variant } = column
  if (!variant) return column

  const variantGroup = variantMap.get(variant.id)
  const hasSibling = variantGroup?.allValues.some((value) => value !== column.value && selectedColumns.has(value))
  if (!hasSibling) return column

  return {
    ...column,
    label: `${column.label} by ${capitalizeWords(variant.option)}`,
  }
}

export function getVariantDisplayLabel(column: ReportColumnDefinition, allColumns: ReportColumnDefinition[]): string {
  const { variant } = column
  if (!variant) return column.label

  const hasSibling = allColumns.some(
    (c) => c.variant && c.variant.id === variant.id && c.variant.option !== variant.option
  )
  if (!hasSibling) return column.label

  return `${column.label} (${capitalizeWords(variant.option)})`
}

export function getSelectedVariantDisplayLabel({
  column,
  selectedColumns,
  variantMap,
}: {
  column: ReportColumnDefinition
  selectedColumns: Set<string>
  variantMap: Map<string, ReportColumnVariantGroup>
}): string {
  return addVariantToColumnLabel(column, selectedColumns, variantMap).label
}

export function getSelectedReportColumnDisplayLabel({
  value,
  selectedColumns,
  columnsMap,
  variantMap,
}: {
  value: string
  selectedColumns: Set<string>
  columnsMap: Map<string, ReportColumnDefinition>
  variantMap: Map<string, ReportColumnVariantGroup>
}): string {
  const column = columnsMap.get(value)
  if (!column) return value
  return getSelectedVariantDisplayLabel({ column, selectedColumns, variantMap })
}
